import { type ReactNode } from 'react';
import { UserMenu, useOptionalAuth } from '@aliza/auth';
import { NotificationBell } from '@aliza/notifications';
import { Icon, type IconName } from '@aliza/ui';

export interface NavItem {
  label: string;
  path: string;
  icon: IconName;
}

export interface MainLayoutProps {
  children: ReactNode;
  currentPath: string;
  onNavigate: (path: string) => void;
  notificationCount?: number;
  pendingScoreCount?: number;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Home', path: '/home', icon: 'home' },
  { label: 'Friends', path: '/friends', icon: 'friends' },
  { label: 'Chat', path: '/chat', icon: 'chat' },
  { label: 'Leaderboards', path: '/leaderboards', icon: 'trophy' },
  { label: 'Profile', path: '/profiles/me', icon: 'settings' },
];

export function MainLayout({ children, currentPath, onNavigate, notificationCount = 0, pendingScoreCount = 0 }: MainLayoutProps) {
  const { user } = useOptionalAuth();

  return (
    <div data-testid="main-layout" style={{ display: 'flex', minHeight: '100vh', background: '#050508' }}>
      {/* Desktop sidebar */}
      <nav
        data-testid="sidebar"
        aria-label="Main navigation"
        className="desktop-only"
        style={{
          width: 240,
          borderRight: '1px solid rgba(255,255,255,0.04)',
          padding: '20px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          background: 'rgba(12, 12, 18, 0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        {/* Logo */}
        <div
          style={{
            fontFamily: "'Syne', sans-serif",
            fontWeight: 800,
            fontSize: 20,
            marginBottom: 28,
            paddingLeft: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span
            style={{
              background: 'linear-gradient(135deg, #00e5a0, #00b880)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Aliza
          </span>
          <span style={{ color: '#eeeef0' }}>Games</span>
        </div>

        {/* Nav items */}
        {NAV_ITEMS.map((item) => {
          const isActive = currentPath.startsWith(item.path);
          return (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              data-active={isActive ? 'true' : 'false'}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onNavigate(item.path)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                textAlign: 'left',
                background: isActive ? 'rgba(0, 229, 160, 0.06)' : 'transparent',
                border: 'none',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
                color: isActive ? '#00e5a0' : '#8888a0',
                transition: 'all 0.2s ease',
                position: 'relative',
                fontFamily: "'Outfit', sans-serif",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.03)';
                  (e.currentTarget as HTMLButtonElement).style.color = '#eeeef0';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  (e.currentTarget as HTMLButtonElement).style.color = '#8888a0';
                }
              }}
            >
              {/* Active indicator */}
              {isActive && (
                <div
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 3,
                    height: 20,
                    borderRadius: 3,
                    background: '#00e5a0',
                    boxShadow: '0 0 8px rgba(0, 229, 160, 0.4)',
                  }}
                />
              )}
              <Icon name={item.icon} size={18} color={isActive ? '#00e5a0' : '#55556a'} />
              {item.label}
            </button>
          );
        })}

        {/* Sidebar footer with user controls */}
        {user && (
          <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
            <UserMenu onProfileClick={() => onNavigate('/profiles/me')} />
          </div>
        )}
      </nav>

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top header bar */}
        <header
          data-testid="layout-header"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
            padding: '10px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            minHeight: 52,
            background: 'rgba(5, 5, 8, 0.8)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          {pendingScoreCount > 0 && (
            <span
              data-testid="pending-scores-badge"
              style={{
                padding: '5px 12px',
                borderRadius: 9999,
                background: 'rgba(255, 179, 71, 0.1)',
                color: '#ffb347',
                fontSize: 12,
                fontWeight: 600,
                border: '1px solid rgba(255, 179, 71, 0.15)',
              }}
            >
              {pendingScoreCount} score{pendingScoreCount === 1 ? '' : 's'} pending sync
            </span>
          )}
          {user && (
            <>
              <NotificationBell count={notificationCount} />
              <div className="mobile-only">
                <UserMenu onProfileClick={() => onNavigate('/profiles/me')} />
              </div>
            </>
          )}
        </header>

        <main data-testid="main-content" style={{ flex: 1, padding: 28, paddingBottom: 88, overflowY: 'auto' }}>
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav
        data-testid="bottom-nav"
        aria-label="Main navigation"
        className="mobile-only"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          borderTop: '1px solid rgba(255,255,255,0.04)',
          background: 'rgba(5, 5, 8, 0.9)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          padding: '8px 0 max(8px, env(safe-area-inset-bottom))',
        }}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = currentPath.startsWith(item.path);
          return (
            <button
              key={item.path}
              data-testid={`tab-${item.label.toLowerCase()}`}
              data-active={isActive ? 'true' : 'false'}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onNavigate(item.path)}
              style={{
                background: 'none',
                border: 'none',
                padding: '6px 12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                transition: 'color 0.2s',
              }}
            >
              <div style={{ position: 'relative' }}>
                <Icon name={item.icon} size={20} color={isActive ? '#00e5a0' : '#55556a'} />
                {isActive && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -4,
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: '#00e5a0',
                      boxShadow: '0 0 6px rgba(0, 229, 160, 0.5)',
                    }}
                  />
                )}
              </div>
              <span
                style={{
                  fontSize: 10,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#00e5a0' : '#55556a',
                  letterSpacing: '0.02em',
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
